// apiKeys.mjs — /admin/api-keys 的纯逻辑（第八十批「用户 API key」）。
//
// 页面只做渲染，判断在这里，理由与 adminSettings.mjs 同：这几件事**在界面上
// 长得差不多，错了却完全不同**——
//   · "我把它收回了"（revoked）与"它自己到期了"（expired）：前者是有人做了动作、
//     后者是没人做动作。合并成一个"无效"，事故复盘时就无从分辨凭据是被处置过
//     还是被遗忘了。
//   · "从没被用过"（lastUsedAtMs 为 null）与"很久没被用过"：前者说明这枚 key
//     可能压根没交到人手上（该重签），后者说明它闲置（该收回）。
// 纯函数 ⇒ 可穷举单测，而这几态各自的边界（到期那一毫秒）正是最容易差一位的地方。

// 一枚 key 当下的状态。优先级 revoked > expired > active：一枚**先到期后被吊销**
// 的 key 首先是"被收回的"——吊销是人做的决定，到期只是时间到了。
export function keyState(k, nowMs) {
  if (!k || !k.enabled) return 'revoked';
  // 与后端 ApiKeyStore::verify 同一口径：`now >= expires` 即已过期
  // （到点那一毫秒判死）。两处不同口径的"过期"会差出一毫秒，而那种偏差
  // 没有任何症状——直到有人在到期瞬间发请求。
  if (k.expiresAtMs != null && nowMs >= k.expiresAtMs) return 'expired';
  return 'active';
}

// 状态 → i18n 键 / MUI 颜色。放在这里而不是页面里，是为了让"三态各自可见"
// 这件事本身也被单测钉住（少一态就是少一种可分辨的现实）。
export const KEY_STATE_LABEL = {
  active:  'admin.apiKeys.active',
  expired: 'admin.apiKeys.expired',
  revoked: 'admin.apiKeys.revoked',
};

export const KEY_STATE_COLOR = {
  active:  'success',
  expired: 'warning',
  revoked: 'default',
};

// ── 第一百零九批 C2/D：签发响应的两种形态 ───────────────────────────────────
// 裁定 1：管理员勾「不当时显示」⇒ 系统自动签一件**指名给该账户**的定向密递，
// 而响应里 `key` 这个键**整个不出现**（不是空串）。
// 为什么这件事值一只纯函数：`'key' in resp` 与 `resp.key` 的真假在页面上长得一模一样，
// 而错了的后果是把一个**可以复制的空框**摆在管理员面前——他会以为自己复制到了什么，
// 然后把空剪贴板发过去，而那枚 key 已经在密件里、72 小时后过期。后端的注释专门写了这
// 一条，这里给它配一个判据。
//   'delivered' —— 递成了密件（页面显示密件号 + 让他去哪取，绝不显示明文，因为没有）
//   'plaintext' —— 常规签发，明文只此一次
//   'none'      —— 两者都没有：不是"空"，是**这次签发的结果说不清**，要当异常提示，
//                  绝不能悄悄渲染成一个空的复制框。
export function issueResultKind(resp) {
  if (!resp || typeof resp !== 'object') return 'none';
  if (resp.delivery && typeof resp.delivery === 'object' && resp.delivery.id != null) {
    return 'delivered';
  }
  return typeof resp.key === 'string' && resp.key.length > 0 ? 'plaintext' : 'none';
}

// ── SB-148 C：onboarding 快捷发送（模板站内信）───────────────────────────────
// 按钮出现的行：lastUsedAtMs 为空 **且** key 当下生效（active）。
// 前一半是 SB-148 §1C/§3 的字面判据：为空 = 已签发未使用 = 收件人多半还没把 CLI
// 跑起来，正是 onboarding 信有用的那一刻；用过一次说明路已通，再发是打扰。
// 后一半是对字面判据的**登记在案的收窄**（SB-148 §4 处理记录）：一枚已吊销/已到期
// 的 key 也可能从未被用过，照字面判会在它旁边摆出按钮，发出去的信指着一枚死 key
// ——本仓对提示位的既有裁量是「说错话的上下文比没有上下文更糟」（锚点判据同源），
// 一封自信地指错路的 onboarding 信正是那种话。收窄写进判据本身而非页面私加，
// 批报告列为待复核项，裁回字面判据时只改这一处。
// == null 同时吞 null/undefined（老后端缺这一列 = 从未用过）；0 是真时刻，
// 与页面上 fmtMs 的 `k.lastUsedAtMs == null` 同一条口径。
export function mayOfferOnboarding(key, nowMs) {
  return Boolean(key) && key.lastUsedAtMs == null && keyState(key, nowMs) === 'active';
}

// 模板端点的请求体（后端契约：POST /api/admin/messages/template）。
// **只有服务端认识的字段**（composePayload 的同一条纪律）；template 是登记过的
// 模板名而不是自由正文——绕排队的资格来自「内容是审过的模板」。context 只带
// keyLabel 这个提示位：key 明文从头到尾不经过这条路（它走密件）。
export const ONBOARDING_TEMPLATE = 'cli-onboarding';
export function onboardingPayload(account, key) {
  return {
    toAccount: typeof account?.email === 'string' ? account.email : '',
    template: ONBOARDING_TEMPLATE,
    context: { keyLabel: typeof key?.label === 'string' ? key.label : '' },
  };
}

// ── 第一百八十三批：每把 key 的客户端版本（旧版本客户端只做软提醒）────────────
// 需求方裁（2026-08-28）：旧版 dc-cli **不拒请求、不 426**，曝光面是这一列——
// 管理员在列表上一眼看到谁还在用旧版，再去人对人地提醒。所以这里的输出只有
// 三种徽记，没有任何"拦"的语义。
//
// 后端在 api_key.last_client 记的是最近一次请求的 UA：dc-cli 的形如 "dc-cli/1.1.2"；
// 非 dc-cli 的 UA 记消毒后的前 40 字节（curl/浏览器/脚本）。后者照原样显示但**不判
// 版本**——它根本不是 CLI，"落后"对它没有意义。
const DC_CLI_UA = /^dc-cli\/(\d+\.\d+\.\d+)$/;
export function parseClientVersion(lastClient) {
  if (typeof lastClient !== 'string') return null;
  const m = DC_CLI_UA.exec(lastClient.trim());
  return m ? m[1] : null;
}

// 三段整数逐段比（三方共用的口径：服务端 / CLI whoami / 这里必须一致）。
// 返回 -1/0/1；任一侧不是 x.y.z 形状 ⇒ null，调用方 fail-closed（不出徽记）。
// 刻意不做 semver 的预发布/构建元数据：本仓 version.mjs 从来只有三段，接受更宽的
// 形状只会让「1.2.0-beta 算不算低于 1.2.0」这种没人裁过的问题悄悄有了答案。
const SEG3 = /^(\d+)\.(\d+)\.(\d+)$/;
function segs(v) {
  const m = typeof v === 'string' ? SEG3.exec(v.trim()) : null;
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}
export function compareVersions(a, b) {
  const x = segs(a);
  const y = segs(b);
  if (!x || !y) return null;
  for (let i = 0; i < 3; i++) {
    if (x[i] !== y[i]) return x[i] < y[i] ? -1 : 1;
  }
  return 0;
}

// 徽记判定。优先级 upgrade > behind > ok：低于最低支持版首先是"需升级"（红），
// 只低于当前版是"落后"（黄）；不是 dc-cli 或没有参照 ⇒ 'none'（只显示原文，不判）。
// 参照缺一半也照判另一半：只有 min 没有 current 时仍能标红，反之仍能标黄——
// 两者来源不同（头 vs 清单），各自可能单独缺席。
export function clientBadge(lastClient, refs) {
  const v = parseClientVersion(lastClient);
  if (!v) return 'none';
  const min = refs?.min;
  const cur = refs?.current;
  if (min && compareVersions(v, min) === -1) return 'upgrade';
  if (cur && compareVersions(v, cur) === -1) return 'behind';
  return 'ok';
}

// 'ok' 与 'none' 都不出 Chip——键表只给会显示的两态，而两态各自的颜色要被单测钉住
// （红黄对调就是把"需升级"说成"落后"）。
export const CLIENT_BADGE_LABEL = {
  behind:  'admin.apiKeys.client.behind',
  upgrade: 'admin.apiKeys.client.upgrade',
};
export const CLIENT_BADGE_COLOR = {
  behind:  'warning',
  upgrade: 'error',
};

// 参照的合成：当前版取清单 version（页面本来就读它），缺席时回落到应答头
// X-DC-CLI-Current-Version；最低支持版取清单 minSupportedVersion，缺席时回落到
// X-DC-CLI-Min-Version。清单优先是因为它是构建期写下的那一份，头是服务端启动时
// 读的同一份——两者不一致只会在部署中途出现，那时以清单为准更不容易误标。
export function cliVersionRefs(manifest, hints) {
  const pick = (a, b) => (typeof a === 'string' && a.trim() ? a.trim()
    : typeof b === 'string' && b.trim() ? b.trim() : null);
  return {
    current: pick(manifest?.version, hints?.current),
    min: pick(manifest?.minVersion, hints?.min),
  };
}

// 表单里的"多少天后过期"→ 请求体的 expiresInDays。
// 空 / 非数字 / 0 / 负数一律归 0 = **不过期**，而不是"立刻过期"：
// 一个把输入框留空就签出一枚当场失效的 key 的表单，只会让人以为签发坏了。
export function expiresInDaysFrom(input) {
  const n = Number(input);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}
